import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crosshair, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepSection } from '@/components/step-section';
import { TextField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import {
  use_draft,
  use_save_address,
  use_saved_address,
} from '@/features/onboarding/hooks';

type Form = {
  address: string;
  landmark: string;
  city: string;
  state: string;
  pincode: string;
  latitude: string;
  longitude: string;
};

const EMPTY: Form = {
  address: '',
  landmark: '',
  city: '',
  state: '',
  pincode: '',
  latitude: '',
  longitude: '',
};

const PINCODE_REGEX = /^[1-9][0-9]{5}$/;

function validate(form: Form): Record<string, string> {
  const errors: Record<string, string> = {};
  if (form.address.trim().length < 5) {
    errors.address = 'Street address must be at least 5 characters.';
  }
  if (form.city.trim().length < 2) {
    errors.city = 'City is required.';
  }
  if (form.state.trim().length < 2) {
    errors.state = 'State is required.';
  }
  if (!PINCODE_REGEX.test(form.pincode)) {
    errors.pincode = 'Enter a 6-digit pincode (can’t start with 0).';
  }
  const lat = Number(form.latitude);
  const lon = Number(form.longitude);
  if (form.latitude.trim() === '' || !Number.isFinite(lat) || lat < -90 || lat > 90) {
    errors.latitude = 'Missing or invalid. Tap "Use my location" above.';
  }
  if (form.longitude.trim() === '' || !Number.isFinite(lon) || lon < -180 || lon > 180) {
    errors.longitude = 'Missing or invalid. Tap "Use my location" above.';
  }
  return errors;
}

export function AddressPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_saved_address({ enabled: !!draft_q.data });
  const save_m = use_save_address();
  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});
  const [locating, set_locating] = useState(false);

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.basic_info_completed) {
      navigate('/onboarding/basic-info', { replace: true });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_form({
        address: saved_q.data.address,
        landmark: saved_q.data.landmark ?? '',
        city: saved_q.data.city,
        state: saved_q.data.state,
        pincode: saved_q.data.pincode,
        latitude: String(saved_q.data.latitude),
        longitude: String(saved_q.data.longitude),
      });
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
    // Clear the field's error as soon as the user starts fixing it.
    set_field_errors((fe) => {
      if (!(key in fe)) return fe;
      const next = { ...fe };
      delete next[key];
      return next;
    });
  }

  function use_my_location() {
    set_error(null);
    if (!navigator.geolocation) {
      set_error('Your browser doesn’t support location. Enter coordinates from Google Maps instead.');
      return;
    }
    set_locating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set_locating(false);
        // Backend stores Decimal(10,7) — round to 7 places.
        set_form((f) => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(7),
          longitude: pos.coords.longitude.toFixed(7),
        }));
        set_field_errors((fe) => {
          const next = { ...fe };
          delete next.latitude;
          delete next.longitude;
          return next;
        });
      },
      (geo_err) => {
        set_locating(false);
        set_error(
          geo_err.code === geo_err.PERMISSION_DENIED
            ? 'Location permission denied. Allow it in your browser, or open Google Maps, long-press your shop, and copy the two numbers shown.'
            : 'Couldn’t get your location. Try again, or copy the coordinates from Google Maps.',
        );
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    const client_errors = validate(form);
    if (Object.keys(client_errors).length > 0) {
      set_field_errors(client_errors);
      set_error('Please fix the highlighted fields below.');
      return;
    }
    set_field_errors({});

    try {
      await save_m.mutateAsync({
        address: form.address.trim(),
        landmark: form.landmark.trim() || undefined,
        city: form.city.trim(),
        state: form.state.trim(),
        pincode: form.pincode,
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
      });
      navigate('/onboarding/uploads');
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save address', { highlights_fields: true })
          : 'Could not save address',
      );
    }
  }

  function use_bangalore() {
    set_field_errors({});
    set_form({
      address: '4th Cross, Indiranagar',
      landmark: 'Near 100ft Road',
      city: 'Bangalore',
      state: 'Karnataka',
      pincode: '560038',
      latitude: '12.9784',
      longitude: '77.6408',
    });
  }

  return (
    <form onSubmit={handle_submit} noValidate>
      <StepSection
        step_key="address"
        title="Address & location"
        description="Used for nearby-search ranking and the map pin shown to customers."
        error={error}
        footer={
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={use_bangalore} data-testid="prefill-bangalore">
              Prefill Bangalore
            </Button>
            <Button
              type="submit"
              variant="gradient"
              disabled={save_m.isPending}
              data-testid="address-submit"
            >
              {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save & continue
            </Button>
          </div>
        }
      >
        <TextField
          label="Street address"
          name="address"
          data-testid="field-address"
          value={form.address}
          onChange={(e) => update('address', e.target.value)}
          maxLength={500}
          placeholder="12 MG Road"
          error={field_errors.address}
        />
        <TextField
          label="Landmark"
          name="landmark"
          data-testid="field-landmark"
          value={form.landmark}
          onChange={(e) => update('landmark', e.target.value)}
          maxLength={200}
          placeholder="Opposite ABC Mall"
          optional
          error={field_errors.landmark}
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <TextField
            label="City"
            name="city"
            data-testid="field-city"
            value={form.city}
            onChange={(e) => update('city', e.target.value)}
            maxLength={100}
            error={field_errors.city}
          />
          <TextField
            label="State"
            name="state"
            data-testid="field-state"
            value={form.state}
            onChange={(e) => update('state', e.target.value)}
            maxLength={100}
            error={field_errors.state}
          />
          <TextField
            label="Pincode"
            name="pincode"
            data-testid="field-pincode"
            value={form.pincode}
            onChange={(e) => update('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            placeholder="560038"
            error={field_errors.pincode}
          />
        </div>

        <div className="rounded-lg border border-border bg-card/60 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-heading text-sm font-semibold">Map location</h3>
              <p className="text-xs text-muted-foreground">
                This places your pin on the customer map. Easiest: tap the button while
                you're at the shop.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={use_my_location}
              disabled={locating}
              data-testid="use-my-location"
            >
              {locating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Crosshair className="h-4 w-4" />
              )}
              {locating ? 'Locating…' : 'Use my location'}
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <TextField
              label="Latitude"
              name="latitude"
              data-testid="field-lat"
              value={form.latitude}
              onChange={(e) => update('latitude', e.target.value)}
              inputMode="decimal"
              placeholder="12.9784"
              hint="Or: open Google Maps, long-press your shop, copy the first number."
              error={field_errors.latitude}
            />
            <TextField
              label="Longitude"
              name="longitude"
              data-testid="field-lon"
              value={form.longitude}
              onChange={(e) => update('longitude', e.target.value)}
              inputMode="decimal"
              placeholder="77.6408"
              hint="…and the second number."
              error={field_errors.longitude}
            />
          </div>
        </div>
      </StepSection>
    </form>
  );
}
